import { useMemo, useState } from "react"
import { Route } from "@/routes/(app)/_layout/inventory/$itemId/index"
import { sampleItem } from "@/routes/(app)/_layout/inventory/$itemId/-data/fixtures"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { ActivityIcon, BarChart2, Boxes, BadgeCheck, Users, Package as PackageIcon, RefreshCcw, ArrowUpRight, AlertTriangle } from "lucide-react"
import Header from "./header"
import CustomTabs from "./custom-tabs"
import Timeline from "./timeline"
import RightInfoTabs from "./right-info-tabs"
import RightInfoContent from "./right-info-content"
import Stocks from "./stocks"

export default function Screen() {
  const params = Route.useParams()
  const item = useMemo(() => sampleItem, [params.itemId])
  const [activeTab, setActiveTab] = useState("Activity")
  const [rightInfoTab, setRightInfoTab] = useState<"overview" | "gtins">("overview")
  const alternatives = [
    { name: "Aspirin 500mg", code: "ASP-500-001", percent: 78, manufacturer: "Bayer", units: 850, tag: "Same Category" },
    { name: "Paracetamol 500mg", code: "PAR-500-001", percent: 92, manufacturer: "GSK", units: 1100, tag: "Same Category" },
    { name: "Naproxen 250mg", code: "NAP-250-001", percent: 68, manufacturer: "Roche", units: 650, tag: "Similar NSAID" },
  ]
  const TABS = [
    { label: "Activity", icon: <ActivityIcon className="w-3 h-3" /> },
    { label: "Metrics", icon: <BarChart2 className="w-3 h-3" />, count: item.metricsCount || 0 },
    { label: "Stocks", icon: <Boxes className="w-3 h-3" />, count: item.stocksCount || 0 },
    { label: "UPP", icon: <BadgeCheck className="w-3 h-3" /> },
  ]

  return (
    <div className="h-full overflow-hidden mt-2">
      <Header itemName={item.name} lpo={item.lpoCode} shelf={item.shelf} />
      <Badge variant="secondary" className="bg-blue-50 text-blue-700 border border-blue-200 mb-2">UPP {item.uppStatus}</Badge>
      <hr className="" />
      <div className="grid grid-cols-5 gap-0">
        <div className="col-span-3 pr-3">
          <CustomTabs tabs={TABS} activeTab={activeTab} onTabClick={setActiveTab} />
        </div>
        <div className="col-span-2 pl-3">
          <RightInfoTabs subTab={rightInfoTab} onChange={setRightInfoTab} />
        </div>
      </div>
      <hr />
      <div className="grid grid-cols-5 gap-0 divide-x divide-gray-200 min-h-0 h-[calc(100vh-200px)]">
        <div className="col-span-3 overflow-y-auto pr-3">
          {activeTab === "Activity" && (
            <>
              <h2 className="text-lg font-semibold mb-2">Recent Activity</h2>
              <Timeline />
            </>
          )}
          {activeTab === "Metrics" && (
            <div className="p-3">
              <div className="grid grid-cols-2 gap-3">
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm">Current Stock</div>
                        <div className="mt-2 text-3xl font-semibold">1,250</div>
                        <div className="text-xs text-muted-foreground mt-1">Units in warehouse</div>
                      </div>
                      <Users className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm">Expiry Status</div>
                        <div className="mt-2 text-3xl font-semibold">45</div>
                        <div className="mt-1 text-xs text-orange-500 flex items-center"><AlertTriangle className="h-3 w-3 mr-1" /> Units expiring soon in 30 days</div>
                      </div>
                      <PackageIcon className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm">ReOrder Level</div>
                        <div className="mt-2 text-3xl font-semibold">200</div>
                        <div className="text-xs text-muted-foreground mt-1">Threshold Set by Pharma Manager</div>
                      </div>
                      <RefreshCcw className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-sm">Availability</div>
                        <div className="mt-2 text-3xl font-semibold">85%</div>
                        <div className="text-xs text-muted-foreground mt-1">Stock Level Status</div>
                      </div>
                      <ArrowUpRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              </div>
              <div className="mt-6">
                <div className="text-sm font-medium mb-3">Alternatives</div>
                <div className="rounded-2xl border border-gray-200 overflow-hidden">
                  {alternatives.map((alt, idx) => (
                    <div key={alt.code} className={"p-4 " + (idx < alternatives.length - 1 ? "border-b border-gray-200" : "")}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="text-base font-medium">{alt.name}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{alt.code}</div>
                          <div className="text-xs text-muted-foreground mt-1">{alt.percent}%</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-muted-foreground">{alt.manufacturer}</div>
                          <div className="text-xs text-muted-foreground mt-0.5">{alt.units} units</div>
                        </div>
                      </div>
                      <div className="mt-2 text-right">
                        <span className="inline-block rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-blue-600">{alt.tag}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-3 text-xs text-muted-foreground">
                  Alternatives are shown based on: Category, Manufacturer, Active Ingredient, therapeutic and Dosage Form similarity.
                </div>
              </div>
            </div>
          )}
          {activeTab === "Stocks" && <Stocks />}
          {activeTab === "UPP" && (
            <div className="p-6 text-sm text-muted-foreground">UPP Content</div>
          )}
        </div>
        <div className="col-span-2 overflow-y-auto pl-3 pt-3">
          <RightInfoContent subTab={rightInfoTab} />
        </div>
      </div>
    </div>
  )
}
